import React, { useEffect, useState } from 'react';
import LoadingSpinner from 'views/components/loadingSpinner/loadingSpinner';
import { Card, CardBody, CardHeader, CardTitle, Col, Row, Modal, ModalBody, ModalHeader } from 'reactstrap';
import useLoading from 'hooks/useLoading';
import MYDocumentCollection from 'lib/common/models/myDocumentCollection';
import useTableControls from 'hooks/useTableControls';
import { stateListNames } from 'store/reducers/listsReducer';
import MYDocument, { MYDocumentStatus } from 'lib/common/models/myDocument';
import { NZDocumentType } from 'lib/common/models/nzDocument';
import { DEFAULT_TIMEZONE_FORMAT, formatDateTime, sortDateTable } from 'shared/utils/dateTime';
import { useDispatch, useSelector } from 'react-redux';
import { DropdownButton, Dropdown } from 'react-bootstrap';
import { AppState } from 'store/store';
import { toast, ToastContainer } from 'react-toastify';
import ReactTable from 'components/ReactTable/ReactTable';
import ConfirmationModal from 'views/components/confirmationModal/confirmationModal';
import { useHistory } from 'react-router';
import NavigationExamApi from 'api/NavigationExamApi';
import ApiCaller from 'lib/ApiCaller';
import Exam from 'lib/common/models/exam';
import { setCurrentDoc, setCurrentExam } from 'store/actions/docs/docsActions';
import ExamDocApi from 'api/ExamDocApi';
import './style.css';
import { is } from 'shared/utils/validations';
import { set } from 'lodash';


const ExamDocumentVerification: React.FC = () => {
    const listName = stateListNames.examPool;
    const loggedUser = useSelector((state: AppState) => state.session.userInfo);
    const layout = useSelector((state: AppState) => state.session.layout);
    const history = useHistory();
    const dispatch = useDispatch();
    const {loading,setLoading,loadingTip,setLoadingTip} = useLoading()
    const [documentList, setDocumentList] = useState<MYDocumentCollection>(new MYDocumentCollection([]));
    const {
        filters,
        sorting,
        pagination,
        onChangeFilters,
        onChangeSorting,
        onChangePagination,
      } = useTableControls(listName);
    const [isRejectExamModalOpen, setIsRejectExamModalOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<MYDocument | null>(null);
    const [rejectReason, setRejectReason] = useState<string>('');
    const [isApproveConfirmationOpen, setIsApproveConfirmationOpen] = useState<boolean>(false);
      // Api classes
      const navigationExamApi = new NavigationExamApi(new ApiCaller(loggedUser.token));
      const docsApi = new ExamDocApi(new ApiCaller());

      /*
      * Data loaders
      */
      const refreshDocuments = () => {
        setLoading(true);
        setLoadingTip('Loading exams...');
        docsApi
          .loadDraftExamsByStatuses([MYDocumentStatus.AWAITING_MCA_VALIDATION], loggedUser.token)
          .then((data) => {
            setDocumentList(data);
          })
          .catch((err) => {
            console.error(err);
            toast.error('Error loading exams');
          })
          .finally(() => {
            setLoading(false);
          });
      };

      useEffect(() => {
        refreshDocuments();
      }, []);


      /**
       * Handlers
       */
      const handlePreviewPDF = (document: MYDocument) => {
        setLoading(true);
        setLoadingTip('Generating PDF preview...');
        docsApi
          .sendToWritePDF(document.id)
          .then((response) => {
            // Create a blob from the PDF data
            const blob = new Blob([response], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            
            // Open PDF in new window
            const pdfWindow = window.open(url, '_blank');
            if (!pdfWindow) {
              toast.error('Please allow pop-ups to view the PDF');
            }
            
            // Clean up the URL after a short delay
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
          })
          .catch((err) => {
            console.error(err);
            toast.error('Error generating PDF preview');
          })
          .finally(() => {
            setLoading(false);
          });
      };

      const handleApprove = (document: MYDocument) => {
        setLoadingTip('Approving...');
        setLoading(true);
        docsApi
          .approveMCADocument(document.id, loggedUser.token)
          .then(() => {
            toast.success('Document approved');
            refreshDocuments();
          })
          .catch((err) => {
            console.error(err);
            toast.error('Error approving document');
          })
          .finally(() => {
            // refreshDocuments handles loading state; ensure spinner stops if refresh failed early
            setLoading(false);
          });
      };

      const handleOpenRejectModal = (document: MYDocument) => {
        setSelectedDocument(document);
        setRejectReason(''); // Reset the rejection reason text
        setIsRejectExamModalOpen(true);
      };

      const handleReject = (document: MYDocument) => {
        setLoadingTip('Rejecting...');
        setLoading(true);
        docsApi
          .refuseMCADocument(document.id, rejectReason, loggedUser.token)
          .then(() => {
            toast.success('Document rejected successfully');
            setIsRejectExamModalOpen(false);
            setRejectReason(''); // Reset after successful rejection
            refreshDocuments();
          })
          .catch((err) => {
            console.error(err);
            toast.error('Error rejecting document');
          })
          .finally(() => {
            // refreshDocuments handles loading state; ensure spinner stops if refresh failed early
            setLoading(false);
          });
      };

      const columns = [
        {
          Header: 'SERIAL NO.',
          accessor: (data: MYDocument) =>
            data.serialNumber ? data.serialNumber : '---',
          sortable: true,
        },
        { Header: 'EXAM ID', accessor: (data: MYDocument) => (data.type === NZDocumentType.EXAM_POOL ? '--' : (data.customName ?? data.displayName ?? '---')), sortable: true },
        { Header: 'EXAM CENTRE', accessor: (data: MYDocument) => (data.type === NZDocumentType.EXAM_POOL ? '--' : (data.customDescription ?? '---')), sortable: true },
        {
          Header: 'LAST EDIT',
          accessor: (data: MYDocument) =>
            formatDateTime(data.updatedAt, DEFAULT_TIMEZONE_FORMAT),
          sortable: true,
          sortType: (a: any, b: any) => sortDateTable(a, b, 'updatedAt'),
        },
        {
          Header: 'ACTIONS',
          Cell: (row: any) => {
            const document: MYDocument = row.row.original;
            return (
              <DropdownButton
              variant="default" title="Actions" size="sm">

                <Dropdown.Item
                    style={{ margin: 0 }}
                    onClick={() => handlePreviewPDF(document)}>
                    <i className="nc-icon nc-cloud-download-93" />
                    &nbsp;&nbsp;PDF PREVIEW
                </Dropdown.Item>
                <Dropdown.Item
                    style={{ margin: 0 }}
                    onClick={() => { setSelectedDocument(document); setIsApproveConfirmationOpen(true); }}>
                    <i className="nc-icon nc-check-2" />
                    &nbsp;&nbsp;APPROVE
                </Dropdown.Item>
                <Dropdown.Item
                    style={{ margin: 0 }}
                    onClick={() => handleOpenRejectModal(document)}>
                    <i className="nc-icon nc-simple-delete" />
                    &nbsp;&nbsp;REJECT
                </Dropdown.Item>
              </DropdownButton>
            );
          },
        },
      ];

    return (
        <>
        <div className="content">
            <LoadingSpinner spinning={loading} tip={loadingTip}>
                <Row>
                    <Col md="12">
                        <Card>
                            <CardHeader>
                                <CardTitle tag="h4">Senior ETO Exams Validation</CardTitle>
                            </CardHeader>
                            <CardBody>
                              {(documentList.length() > 0) && (
                                <ReactTable
                                  data={documentList.documents}
                                  columns={columns}
                                  initialFilters={filters}
                                  onChangeFilters={onChangeFilters}
                                  initialSorting={sorting}
                                  onChangeSorting={onChangeSorting}
                                  initialPagination={pagination}
                                  onChangePagination={onChangePagination}
                                />
                              )}
                              {(documentList.length() === 0) && (
                                <h5 className="card-category">
                                No pending exams to review.
                                </h5>
                              )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </LoadingSpinner>
            <Modal
              className="modal-reject-exam"
              isOpen={isRejectExamModalOpen}
              toggle={() => {
                setIsRejectExamModalOpen(!isRejectExamModalOpen);
                setRejectReason(''); // Reset when modal is toggled closed
              }}>
              <ModalHeader>
                <button
                  aria-hidden={true}
                  className="close"
                  data-dismiss="modal"
                  type="button"
                  onClick={() => {
                    setIsRejectExamModalOpen(false);
                    setRejectReason(''); // Reset when X button is clicked
                  }}>
                  <i className="nc-icon nc-simple-remove" />
                </button>
                <h5 className="modal-title">Reject exam {selectedDocument?.serialNumber}</h5>
              </ModalHeader>
              <ModalBody>
                <Row>
                  <Col md="12">
                    <textarea
                      className="form-control"
                      rows={5}
                      placeholder="Enter reason for rejection"
                      value={rejectReason|| ''}
                      onChange={(e) => { setRejectReason(e.target.value); }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md="12" className="text-right mt-3">
                    <button
                      className="btn btn-danger mr-2"
                      onClick={() => { handleReject(selectedDocument as MYDocument);
                      }}
                    >
                      Reject
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setIsRejectExamModalOpen(false);
                        setRejectReason(''); // Reset when Cancel button is clicked
                      }}
                    >
                      Cancel
                    </button>
                  </Col>
                </Row>
              </ModalBody>
            </Modal>
            <ToastContainer />
            {isApproveConfirmationOpen && selectedDocument && (
              <ConfirmationModal
                title="Approve exam confirmation"
                onConfirm={() => { setIsApproveConfirmationOpen(false); handleApprove(selectedDocument); }}
                onCancel={() => { setIsApproveConfirmationOpen(false); }}
                text={(function() {
                  return 'This will publish the exam and make it available to the Exam Centre when the exam is scheduled.';
                })()}
              />
            )}
        </div>
        </>
    );
};

export default ExamDocumentVerification;
